import {
  Autocomplete,
  AutocompleteOption,
  Icons,
  ListItemContent,
  Stack,
  Typography,
  VariantProp,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getGetAddressContactsQueryKey,
  getGetAllContactsListQueryKey,
  useCreateAddressContact,
  useGetAddressContacts,
  useGetAllContactsList,
} from '../../../api/generated/address-controller/address-controller.ts';
import { AddressContactDto, AddressDto, ContactWithAddressDto } from '../../../api/generated/model';
import { syncContact } from '../../../api/generated/sync-controller/contact-sync-controller.ts';
import { useSyncableSelection } from '../../../hooks/useSyncableSelection.ts';
import { CreateAddressContactDialog } from './CreateAddressContactDialog.tsx';
import { ContactDisplay } from './PatientAddressDisplay.tsx';

interface Props {
  onChange: (contactDto: AddressContactDto | null, address?: AddressDto) => void;
  value: AddressContactDto | null;
  address: AddressDto | null;
  patientId?: string;
  variant?: VariantProp;
  showSyncIcon?: boolean;
  disabled?: boolean;
  useAllContactsList?: boolean;
  createOptionLabel?: string;
  /**
   * The contact as it stands after a sync, which may be null: the backend empties the
   * Ansprechperson when it is no longer linked to the Institution in master data. Separate from
   * [onChange] because this is the server's answer rather than a user edit, so it must not mark the
   * form dirty and trigger an autosave that writes back what was just read.
   */
  onSynced?: (contactDto: AddressContactDto | null) => void;
}

const CREATE_CONTACT_OPTION: AddressContactDto = {
  id: '__create__',
  firstName: 'Kontakt erstellen',
  lastName: '',
  isActive: true,
};

function isSameContact(option: AddressContactDto, selected: AddressContactDto) {
  if (option.id && selected.id) {
    return option.id === selected.id;
  }

  return (
    option.firstName === selected.firstName &&
    option.lastName === selected.lastName &&
    option.function === selected.function &&
    option.phone === selected.phone &&
    option.email === selected.email
  );
}

function fromContactWithAddress(contact: ContactWithAddressDto): AddressContactDto {
  return {
    id: contact.id,
    addressId: contact.address?.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    function: contact.function,
    phone: contact.phone,
    email: contact.email,
    address: contact.address,
    isActive: contact.isActive,
  };
}

export function SelectAddressContact({
  onChange,
  value,
  patientId,
  address,
  variant,
  showSyncIcon = false,
  disabled = false,
  useAllContactsList = false,
  createOptionLabel = 'Neuer Kontakt erstellen',
  onSynced,
}: Props) {
  const queryClient = useQueryClient();
  const addressId = address?.id ? String(address.id) : undefined;
  const addressContactQueryEnabled = !!addressId && !useAllContactsList;
  const allContactsQueryEnabled = useAllContactsList;

  const { data: addressContacts, isPending: areAddressContactsPending } = useGetAddressContacts(
    addressId ?? '',
    { patientId },
    {
      query: { enabled: addressContactQueryEnabled },
    },
  );

  const { data: allContacts, isPending: areAllContactsPending } = useGetAllContactsList(
    { addressId, patientId },
    {
      query: { enabled: allContactsQueryEnabled },
    },
  );

  const [isCreateContactDialogOpen, setIsCreateContactDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState<AddressContactDto | null>(null);

  const createAddressContactMutation = useCreateAddressContact({
    mutation: {
      onError: () => {
        toast.error('Kontakt konnte nicht erstellt werden');
      },
    },
  });

  const filterContactOptions = (options: AddressContactDto[], { inputValue }: { inputValue: string }) =>
    matchSorter(options, inputValue, {
      keys: ['firstName', 'lastName', 'function', 'phone', 'email'],
    });

  const contactOptions = useMemo<AddressContactDto[]>(() => {
    if (useAllContactsList) {
      return (allContacts ?? []).map(fromContactWithAddress);
    }

    return (addressContacts ?? []).map(({ address: _address, ...contact }) => ({
      ...contact,
      addressId: contact.addressId ?? addressId,
    }));
  }, [addressContacts, addressId, allContacts, useAllContactsList]);

  useEffect(() => {
    if (
      contactOptions.length > 0 &&
      newContact &&
      contactOptions.some((contact) => isSameContact(contact, newContact))
    ) {
      setNewContact(null);
    }
  }, [contactOptions, newContact]);

  const { availableOptions, selectedValue, triggerSync, isSyncing } = useSyncableSelection({
    value,
    options: contactOptions,
    pendingOption: newContact,
    isSame: isSameContact,
    sync:
      patientId && addressId
        ? {
            mutationFn: ({ patientId, referenceType }: { patientId: string; referenceType: string }) =>
              syncContact(patientId, referenceType),
            invalidateQueryKey: getGetAddressContactsQueryKey(addressId, { patientId }),
            // The response carries the refreshed snapshot, and `contact` is null when the person
            // no longer belongs to this Institution. Without writing it back the field keeps the
            // old contact: it is controlled, and useSyncableSelection re-adds a selected value that
            // has dropped out of the options.
            onSynced: (patient) => onSynced?.(patient.address?.contact ?? null),
            successMessage: 'Kontakt erfolgreich synchronisiert',
            errorMessage: 'Synchronisierung fehlgeschlagen',
          }
        : undefined,
  });

  return (
    <>
      <Stack gap={1}>
        <Autocomplete
          value={selectedValue}
          disabled={disabled || (!address && !useAllContactsList) || createAddressContactMutation.isPending}
          loading={areAddressContactsPending || areAllContactsPending || createAddressContactMutation.isPending}
          options={availableOptions}
          variant={variant}
          getOptionLabel={(option) => `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim()}
          isOptionEqualToValue={(option, selectedValue) => isSameContact(option, selectedValue)}
          onChange={(_, selectedValue) => {
            if (selectedValue?.id === CREATE_CONTACT_OPTION.id) {
              setIsCreateContactDialogOpen(true);
              return;
            }

            onChange(selectedValue, selectedValue?.address ?? address ?? undefined);
          }}
          filterOptions={(options, state) => {
            const filtered = filterContactOptions(options, state);

            return [CREATE_CONTACT_OPTION, ...filtered];
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props as typeof props & { key: React.Key };

            if (option.id !== CREATE_CONTACT_OPTION.id) {
              return (
                <AutocompleteOption key={key} {...optionProps}>
                  <ListItemContent>
                    <Typography level="title-sm">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography level="body-sm">{option.phone}</Typography>
                  </ListItemContent>
                </AutocompleteOption>
              );
            }

            return (
              <AutocompleteOption key={key} {...optionProps}>
                <ListItemContent>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Icons.Add />
                    <Typography level="title-sm">{createOptionLabel}</Typography>
                  </Stack>
                </ListItemContent>
              </AutocompleteOption>
            );
          }}
        />

        {selectedValue && (
          <div style={{ paddingLeft: 13 }}>
            <ContactDisplay
              contact={selectedValue}
              showNameAsTitle
              showSyncIcon={showSyncIcon && !!patientId}
              onSyncClick={() => {
                if (!patientId) return;
                triggerSync({
                  patientId,
                  referenceType: 'PATIENT_RESIDENCE',
                });
              }}
              syncLoading={isSyncing}
            />
          </div>
        )}
      </Stack>

      <CreateAddressContactDialog
        open={isCreateContactDialogOpen}
        isLoading={createAddressContactMutation.isPending}
        defaultAddress={address}
        isAddressDisabled={!!address?.id}
        onClose={(contactDto) => {
          setIsCreateContactDialogOpen(false);

          if (!contactDto) return;

          void (async () => {
            try {
              const contactId = await createAddressContactMutation.mutateAsync({
                id: contactDto.addressId,
                data: {
                  ...contactDto.contactDto,
                  addressId: contactDto.addressId,
                },
              });

              const createdContact: AddressContactDto = {
                ...contactDto.contactDto,
                id: contactId,
                addressId: contactDto.addressId,
                ...(useAllContactsList ? { address: contactDto.address } : {}),
              };

              setNewContact(createdContact);
              onChange(createdContact, contactDto.address);

              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: getGetAllContactsListQueryKey(),
                }),
                queryClient.invalidateQueries({
                  queryKey: getGetAddressContactsQueryKey(contactDto.addressId),
                }),
              ]);
            } catch {
              // Error toast is already handled by the mutation.
            }
          })();
        }}
      />
    </>
  );
}
